import React, { FunctionComponent, useEffect, useState } from 'react';
import { useTheme } from '@mui/material/styles';
import { useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQuery } from "@tanstack/react-query";
import { useAtom } from 'jotai';
import { useTranslation } from 'react-i18next';
// import useMediaQuery from '@mui/material/useMediaQuery';

// import { check_subdomain } from '../../lib/server_helper';
import { fetch_all_with_count, fetch_one } from "../../../lib/v31lib";
import { loginAtom } from '../../../lib/auth';
import { currentPracticeAtom } from '../../../lib/practice';

import AttachFileIcon from '@mui/icons-material/AttachFile';

import {
  Alert,
  Avatar,
  Box,
  CircularProgress,
  Grid,
} from '@mui/material';

import {
  Page,
  Typography
} from '../../../components/v2/styled';
import { StyledKnowledgeSubject } from '../../../components/v2/styled/knowledge/Subject';
import { StyledKnowledgeLesson } from '../../../components/v2/styled/knowledge/Lesson';

import { KnowledgeCourse } from '../../../models/KnowledgeCourse';
import { KnowledgeSubscription } from '../../../models/KnowledgeSubscription';
import { KnowledgeSubject } from '../../../models/KnowledgeSubject';
import { KnowledgeLesson } from '../../../models/KnowledgeLesson';

import HelpersLanguage from '../../../actions/helpers/language';
import { SnackError } from '../../../components/v2/snacks/Error';
import { StyledKnowledgeQuiz } from '../../../components/v2/styled/knowledge/Quiz';

const { DateTime } = require("luxon");

type Props = {}

export const ClientKnowledgeCourseSubscription: FunctionComponent<Props> = ({}) => {
  const [login, setLogin] = useAtom(loginAtom);
  const [currentPractice, setCurrentPractice] = useAtom(currentPracticeAtom);
  const theme = useTheme();
  const navigate = useNavigate();
  const {t, i18n} = useTranslation(['translations']);
  const params = useParams();

  const [objectId, setObjectId] = useState<string | undefined>(params.id);
  const [object, setObject] = useState<KnowledgeSubscription>();
  const [page, setPage] = useState<number>(0);
  const [order, setOrder] = useState<string>("created_at DESC");
  const [search, setSearch] = useState<string>("");
  const [videoActiveLanguage, setVideoActiveLanguage] = useState<string>(i18n.resolvedLanguage || 'nl');
  const [showError, setShowError] = useState<string>();

  const [destroyedOpen, setDestroyedOpen] = useState<boolean>(false);

  const {
    data: objects,
    isLoading: objectsLoading,
    isError: objectsError,
    isSuccess: objectsSuccess,
    refetch: objectsRefetch,
  } = useQuery({
    queryKey: ["client", "knowledge_sub", objectId, {page: page, order: order, search: search}],
    queryFn: () =>
      fetch_all_with_count<KnowledgeSubscription>(
        `/${i18n.resolvedLanguage || 'nl'}/v3/objects/fetch_all`,
        {
          object: 'knowledge_subscription',
          fields: [
            'id', 'knowledge_course', 'finished_at', 'lesson_key', 'subject_data', 'lesson_data', 'extra_data', 'mollie_data', 
            'ends_at', 'is_approved', 'approved_at'
          ],
          sub_fields: {
            practice: ['id', 'name', 'get_item', 'get_medium'],
            knowledge_course: ['id', 'full_name', 'full_teaser', 'full_body', 'extra_data', 'get_item', 'is_bg_color', 'practice', 'knowledge_subjects', 'knowledge_lessons', 'access_data', 'mollie_data', 'knowsub', 'is_open_order', 'quiz', 'course_type'],
            knowledge_subjects: ['id', 'full_name', 'full_body', 'knowledge_lessons', 'is_open_order', 'extra_data', 'quiz'],
            knowledge_lessons: ['id', 'full_name', 'videos', 'attachments', 'is_video_lesson', 'extra_data', 'quiz', 'access_data'],
            knowsub: ['id', 'created_at'],
            quiz: ['id', 'name', 'extra_data']
          },
          order: order,
          per_page: 1,
          page: 0,
          filter: {
            search: search,
            advanced: {
              lesson_key: objectId
            }
          }
        },
        login
      ),
    select: (d) => {
      // count = d.count;
      return d.result;
    },
    enabled: !!objectId,
  });







  const mutationCustomAction = useMutation({
    mutationFn: (pars: {
      formData: any;
    }) => {
      return fetch_one<{custom_result: any}>(
        `/${i18n.resolvedLanguage || 'nl'}/v3/objects/custom_action`,
        pars.formData,
        login
      );
    },
    onMutate: (pars: {
      formData: any;
    }) => {
      return true;
    },
    onError: (data, variables, context) => {
      return true;
    },
    // @ts-ignore
    onSuccess: (data, variables, context) => {
      if (!!data.custom_result?.url && !data.custom_result?.blank) navigate(data.custom_result?.url);
      if (!!data.custom_result?.url && !!data.custom_result?.blank) window.open(data.custom_result?.url, "_BLANK");
      if (!!data.custom_result?.message) setShowError(data.custom_result?.message);
    },
  });

  const isSubjectDisabled = (subject:KnowledgeSubject , i:number) => {
    let disabled = false;
    if (!object) return true;

    disabled = !(
      (!!object?.knowledge_course?.is_open_order) || 
      (
        !object?.knowledge_course?.is_open_order && 
        (object?.lesson_data?.completed?.subjects || []).indexOf(subject?.id || 0) < 0 && 
        (
          !(object.knowledge_course?.knowledge_subjects || [])[i - 1]?.id ||
          (object?.lesson_data?.completed?.subjects || []).indexOf((object.knowledge_course?.knowledge_subjects || [])[i - 1]?.id || 0) > -1
        )
      )
    );
    if ((object?.lesson_data?.completed?.subjects || []).indexOf(subject?.id || 0) > -1) disabled = true;

    return disabled
  }
  const isLessonDisabled = (subject:KnowledgeSubject, lesson:KnowledgeLesson , i:number, ii:number) => {
    let disabled = false;
    if (!object) return true;
    if (parseInt(lesson?.access_data?.day_from_start || "0", 10) > 0 && DateTime.fromISO(object?.approved_at).plus({day: parseInt(lesson?.access_data?.day_from_start || "0", 10)}).startOf("day") > DateTime.now().startOf("day")) return true;

    disabled = (!!isSubjectDisabled(subject, i) || (!isSubjectDisabled(subject, i) && !(
      (!!subject?.is_open_order) || 
      (
        !subject?.is_open_order && 
        (object?.lesson_data?.completed?.lessons || []).indexOf(lesson?.id || 0) < 0 && 
        (
          !(subject?.knowledge_lessons || [])[ii - 1]?.id ||
          (object?.lesson_data?.completed?.lessons || []).indexOf((subject?.knowledge_lessons || [])[ii - 1]?.id || 0) > -1
        )
      )
    )));
    if ((object?.lesson_data?.completed?.lessons || []).indexOf(lesson?.id || 0) > -1) disabled = true;

    return disabled
  }
  const isLessonQuizDisabled = (subject:KnowledgeSubject, lesson:KnowledgeLesson , i:number, ii:number) => {
    let disabled = false;
    if (!object) return true;

    if ((object?.lesson_data?.completed?.lessons || []).indexOf(lesson?.id || 0) > -1) {
      disabled = (object?.lesson_data?.completed?.quizzes || []).indexOf(lesson?.quiz?.id || 0) > -1
    } else {
      disabled = true
    }

    return disabled
  }
  const isSubjectQuizDisabled = (subject:KnowledgeSubject , i:number) => {
    let disabled = false;
    if (!object) return true;

    if (!!isSubjectDisabled(subject , i)) {
      disabled = (object?.lesson_data?.completed?.quizzes || []).indexOf(subject?.quiz?.id || 0) > -1
    } else {
      disabled = true
    }

    return disabled
  }

  const selectContentWithId = (field?:{[z:string]: string}, extraFallback?:string|JSX.Element) => {
    let correct = (field || {})[HelpersLanguage.mapping_to_id(i18n.resolvedLanguage || 'nl')];
    if (!!correct) return correct;
    let fallback = (field || {})['nl'];
    if (!!fallback) return fallback;
    let anyFallback = Object.values(field || {}).filter(fff => !!fff)[0];
    if (!!anyFallback) return anyFallback;
    if (!!extraFallback) return extraFallback;
    return t("general.translation_fallbacks.no_translation_available")
  }









  const startLesson = (i:number) => {
    mutationCustomAction.mutate({
      formData: {
        object: 'knowledge_subscription',
        class_action: 'custom_api_lesson_start',
        lesson_key: objectId,
        lesson_id: i,
        user_id: login?.id
      }
    });
  }
  const startQuiz = (i:number) => {
    mutationCustomAction.mutate({
      formData: {
        object: 'knowledge_subscription',
        class_action: 'custom_api_quiz_start',
        lesson_key: objectId,
        quiz_id: i,
        user_id: login?.id
      }
    });
  }

  useEffect(() => {
    if (params.id !== 'new') objectsRefetch();
  }, [params.id]);
  useEffect(() => {
    if ((objects || []).length === 1) setObject((objects || [])[0]);
  }, [objects]);
  useEffect(() => {
    setVideoActiveLanguage(i18n.resolvedLanguage || 'nl');
  }, [i18n.resolvedLanguage]);

  return <Page
    title={selectContentWithId(object?.knowledge_course?.full_name || {}, <CircularProgress />)}
    titleCrumbs={[
      {
        title: t("client.menu.knowledge_center"),
        path: "/knowledge_center"
      }
    ]}
  >
    <Grid container spacing={1}>
      {!!object && !(!object.ends_at || (!!object.ends_at && DateTime.fromISO(object.ends_at) > DateTime.now())) && <Grid item xs={12} md={8}>
        <Typography html sx={{
          borderRadius: 1,
          border: '1px solid transparent',
          borderColor: theme.palette.warning.main,
          padding: 2,
          textAlign: 'center'
        }}>{t("client.course.warnings.subscription_ended")}</Typography>
      </Grid>}
      {!!object && (!object.ends_at || (!!object.ends_at && DateTime.fromISO(object.ends_at) > DateTime.now())) && <>
        {!object.is_approved && <Grid item xs={12} md={8}>
          <Typography html sx={{
            borderRadius: 1,
            border: '1px solid transparent',
            borderColor: theme.palette.warning.main,
            padding: 2,
            textAlign: 'center'
          }}>{t("client.course.warnings.not_approved")}</Typography>
        </Grid>}




        {!!object.is_approved && <>
          {(object?.knowledge_course?.access_data?.availability || [1]).map(m => parseInt(m.toString(), 10)).indexOf(HelpersLanguage.mapping_to_id(i18n.resolvedLanguage || 'nl')) < 0 && <Grid item xs={12} md={12}>
            <Typography html sx={{
              borderRadius: 1,
              border: '1px solid transparent',
              borderColor: theme.palette.warning.main,
              padding: 2,
              textAlign: 'center'
            }}>{t("client.course.warnings.wrong_language")}</Typography>
          </Grid>}

          {Object.values(object?.knowledge_course?.extra_data?.video_url || {}).filter(fff => !!fff).length > 0 && <Grid item xs={12} md={6}>
            <video id="video" controls style={{objectFit: 'contain', width: '100%'}}>
              <source src={`${(object?.knowledge_course?.extra_data?.video_url || {})[videoActiveLanguage]}?${videoActiveLanguage}`} type="video/mp4"/>
            </video>
            <Typography sx={{
              textDecoration: 'underline'
            }}>{t("client.course.subtitles.video_available_in")}</Typography>
            <Box sx={{
              height: '40px',
              display: 'flex',
              flexDirection: 'row'
            }}>
              {Object.keys(object?.knowledge_course?.extra_data?.video_url || {}).filter(fff => !!(object?.knowledge_course?.extra_data?.video_url || {})[fff]).map(l => <Avatar
                sx={{
                  backgroundColor: l === videoActiveLanguage ? theme.palette.primary.main : 'white',
                  color: l === videoActiveLanguage ? 'white' : theme.palette.primary.main,
                  border: '1px solid white',
                  borderColor: l === videoActiveLanguage ? 'white' : theme.palette.primary.main,
                  marginRight: 1,
                  cursor: 'pointer'
                }}
                onClick={() => {
                  setVideoActiveLanguage(l);
                  var video = document.getElementById('video');
                  // @ts-ignore
                  video.load();
                }}
              >
                {l}
              </Avatar>)}
            </Box>
          </Grid>}
          <Grid item xs={12} md={!!(object?.knowledge_course?.extra_data?.video_url || {})[i18n.resolvedLanguage || 'nl'] ? 6 : 12}>
            <Typography html clean>{selectContentWithId(object?.knowledge_course?.full_body || {})}</Typography>
          </Grid>

          {object?.knowledge_course?.course_type === 0 && <>
            <Grid item xs={12} md={12} sx={{marginTop: 4, marginBottom: 3}}>
              <Alert severity='info' sx={{
                "& .MuiAlert-message": {
                  width: '100%'
                }
              }}>
                <Grid container spacing={0}>
                  <Grid item xs={12} md={12}>
                    <Typography variant="h5">{t("client.course.titles.next_lesson")}</Typography>
                  </Grid>

                  <Grid item xs={12} sx={{
                    "& > .MuiBox-root:not(:first-child)": {
                      display: 'none'
                    },
                    "& > .MuiBox-root": {
                      backgroundColor: '#f8f8f8'
                    }
                  }}>
                    {!!object.knowledge_course && (object.knowledge_course?.knowledge_subjects || []).map((subject, i) => <>
                      {(subject.knowledge_lessons || []).map((lesson, ii) => <>
                        {!!object.knowledge_course && !isLessonDisabled(subject, lesson, i, ii) && <StyledKnowledgeLesson
                          knowledgeCourse={object.knowledge_course}
                          knowledgeSubject={subject}
                          knowledgeLesson={lesson}
                          i={ii}
                          sx={{
                            paddingLeft: 8,
                          }}
                          disabled={isLessonDisabled(subject, lesson, i, ii)}
                          showAccessDaysMessage={(object?.lesson_data?.completed?.lessons || []).indexOf(lesson?.id || 0) < 0 && parseInt(lesson?.access_data?.day_from_start || "0", 10) > 0 && DateTime.fromISO(object?.approved_at).plus({day: parseInt(lesson?.access_data?.day_from_start || "0", 10)}).startOf("day") > DateTime.now().startOf("day")}
                          showAccessDays={DateTime.fromISO(object?.approved_at).plus({day: parseInt(lesson?.access_data?.day_from_start || "0", 10)}).startOf("day").toFormat("dd/LL/y")}
                          play={!isLessonDisabled(subject, lesson, i, ii)}
                          startLesson={() => {
                            startLesson(lesson?.id || 0);
                          }}
                        />}
                        {!!lesson?.quiz && !!isLessonDisabled(subject, lesson, i, ii) && (object?.lesson_data?.completed?.quizzes || []).indexOf(lesson?.quiz?.id || 0) < 0 && <StyledKnowledgeQuiz
                          questionnaire={lesson?.quiz}
                          play={() => {
                            startQuiz(lesson?.quiz?.id || 0);
                          }}
                        />}
                      </>)}
                      {!!subject?.quiz && !!isSubjectDisabled(subject, i) && (object?.lesson_data?.completed?.quizzes || []).indexOf(subject?.quiz?.id || 0) < 0 && <StyledKnowledgeQuiz
                        questionnaire={subject?.quiz}
                        play={() => {
                          startQuiz(subject?.quiz?.id || 0);
                        }}
                      />}
                    </>)}
                    {!!object?.knowledge_course?.quiz && (object?.lesson_data?.completed?.quizzes || []).indexOf(object?.knowledge_course?.quiz?.id || 0) < 0 &&  (object.knowledge_course?.knowledge_subjects || []).length === (object?.lesson_data?.completed?.subjects || []).length && <StyledKnowledgeQuiz
                      questionnaire={object?.knowledge_course?.quiz}
                      play={() => {
                        startQuiz(object?.knowledge_course?.quiz?.id || 0);
                      }}
                    />}
                    <Box sx={{

                    }}>
                      <Typography sx={{fontStyle:' italic', color: 'lightgrey'}}>{t("client.course.titles.all_lessons_done")}</Typography>
                    </Box>
                  </Grid>
                </Grid>
              </Alert>
            </Grid>
          </>}

          <Grid item xs={12} md={12} sx={{marginTop: 4}}>
            <Typography variant="h5">{t(object?.knowledge_course?.course_type === 1 ? "client.course.titles.knowledge_overview" : "client.course.titles.lesson_overview")}</Typography>
          </Grid>

          {!!object.knowledge_course && (object.knowledge_course?.knowledge_subjects || []).map((subject, i) => <>
            <Grid item xs={12}>
              {!!object.knowledge_course && <StyledKnowledgeSubject
                knowledgeCourse={object.knowledge_course}
                knowledgeSubject={subject}
                i={i}
                sx={{
                  paddingRight: 2,
                  paddingLeft: 8,
                  textAlign: 'justify'
                }}
                disabled={object?.knowledge_course?.course_type === 0 && isSubjectDisabled(subject, i)}
              />}
              {(subject.knowledge_lessons || []).map((lesson, ii) => <>
                <Grid item xs={12} sx={{paddingLeft: "48px !important", marginTop: 1}}>
                  {!!object.knowledge_course && <StyledKnowledgeLesson
                    knowledgeCourse={object.knowledge_course}
                    knowledgeSubject={subject}
                    knowledgeLesson={lesson}
                    i={ii}
                    sx={{
                      paddingLeft: 8,
                    }}
                    showAccessDaysMessage={(object?.lesson_data?.completed?.lessons || []).indexOf(lesson?.id || 0) < 0 && parseInt(lesson?.access_data?.day_from_start || "0", 10) > 0 && DateTime.fromISO(object?.approved_at).plus({day: parseInt(lesson?.access_data?.day_from_start || "0", 10)}).startOf("day") > DateTime.now().startOf("day")}
                    showAccessDays={DateTime.fromISO(object?.approved_at).plus({day: parseInt(lesson?.access_data?.day_from_start || "0", 10)}).startOf("day").toFormat("dd/LL/y")}
                    disabled={object?.knowledge_course?.course_type === 0 && isLessonDisabled(subject, lesson, i, ii)}
                    play={object?.knowledge_course?.course_type === 1 || (object?.knowledge_course?.course_type === 0 && !isLessonDisabled(subject, lesson, i, ii))}
                    startLesson={() => {
                      startLesson(lesson?.id || 0);
                    }}
                    completed={((object?.lesson_data?.completed?.lessons || []).indexOf(lesson?.id || 0) > -1)}
                  />}
                  {!!lesson?.quiz && <StyledKnowledgeQuiz
                    questionnaire={lesson?.quiz}
                    sx={{marginTop: 1, marginLeft: 3}}
                    disabled={object?.knowledge_course?.course_type === 0 && !!isLessonQuizDisabled(subject, lesson, i, ii)}
                    play={!(object?.knowledge_course?.course_type === 1 || (object?.knowledge_course?.course_type === 0 && !isLessonQuizDisabled(subject, lesson, i, ii))) ? undefined : () => {
                      startQuiz(lesson?.quiz?.id || 0);
                    }}
                  />}
                </Grid>
              </>)}
              {!!subject?.quiz && <StyledKnowledgeQuiz
                questionnaire={subject?.quiz}
                sx={{marginTop: 1, marginLeft: 6}}
                disabled={object?.knowledge_course?.course_type === 0 && !!isSubjectQuizDisabled(subject, i)}
                play={!(object?.knowledge_course?.course_type === 1 || (object?.knowledge_course?.course_type === 0 && !isSubjectQuizDisabled(subject, i))) ? undefined : () => {
                  startQuiz(subject?.quiz?.id || 0);
                }}
              />}
            </Grid>
          </>)}
          {!!object.knowledge_course?.quiz && <Grid item xs={12}><StyledKnowledgeQuiz
            questionnaire={object.knowledge_course?.quiz}
            disabled={object?.knowledge_course?.course_type === 0 && (object?.lesson_data?.completed?.quizzes || []).indexOf(object.knowledge_course?.quiz?.id || 0) > -1}
            play={!(object?.knowledge_course?.course_type === 1 || (object?.knowledge_course?.course_type === 0 && (object?.lesson_data?.completed?.quizzes || []).indexOf(object.knowledge_course?.quiz?.id || 0) < 0)) ? undefined : () => {
              startQuiz(object.knowledge_course?.quiz?.id || 0);
            }}
          /></Grid>}







          {/* {!!object && <Grid item xs={12} md={4}>
            <Grid container spacing={1}>
              {!!object.knowledge_course && (object.knowledge_course?.knowledge_subjects || []).map((subject, i) => <>
                <Grid item xs={12}>
                  {!!object.knowledge_course && <StyledKnowledgeSubject
                    knowledgeCourse={object.knowledge_course}
                    knowledgeSubject={subject}
                    i={i}
                    sx={{
                      paddingRight: 2,
                      paddingLeft: 8,
                      textAlign: 'justify'
                    }}
                  />}
                </Grid>
                {(subject.knowledge_lessons || []).map((lesson, ii) => <>
                  <Grid item xs={12} sx={{paddingLeft: "48px !important"}}>
                    {!!object.knowledge_course && <StyledKnowledgeLesson
                      knowledgeCourse={object.knowledge_course}
                      knowledgeSubject={subject}
                      knowledgeLesson={lesson}
                      i={ii}
                      sx={{
                        paddingRight: 2,
                        paddingLeft: 8,
                        textAlign: 'justify'
                      }}
                    />}
                  </Grid>
                </>)}
              </>)}
            </Grid>
          </Grid>} */}
        </>}
      </>}
    </Grid>

    <SnackError
      error={showError}
      open={!!showError}
      setOpen={() => {
        setShowError(undefined);
      }}
    />
  </Page>;
}
